/**
 * Scope 카탈로그 — 프로젝트/모델이 "담당(cover)"하는 계통의 단일 진실 공급원.
 *
 * 2단계 계층(2026-08-18 사용자 결정):
 * - 그룹 3개(`primary`·`secondary`·`bop`)는 종전 값 그대로 — 기존 프로젝트의
 *   `['primary']`는 "그룹 전체 담당"으로 계속 유효하고, 시스템 개요 그림·범례도
 *   그룹 기준.
 * - 그 아래 계통 id는 `그룹/계통`(예 `primary/sg`). PWR 계열 공통 목록 —
 *   루프형/일체형 차이는 배치·컴포넌트 층위라 계통 분류엔 영향이 없다(그림은 일체형
 *   SMR 일반형 — ReactorSystemSVG 주석; label 문구는 저장되지 않으므로 노형 중립으로 고쳐도
 *   저장 데이터에 영향 없음, 2026-09-16).
 *   (PWR 밖의 원자로 형식은 형식별 카탈로그를 상위에 두어 확장 — id의 그룹
 *   접두가 그걸 막지 않는다.)
 *
 * 역할(role) 축은 별도: 제어기·교란원처럼 계통을 "담당"하지 않는 참가자를
 * 위한 값. `control`/`scenario` 참가자의 scope는 "작용 계통"(선택)이고
 * 커버리지 계산에서 제외된다.
 */

/** 상위 그룹 — 종전 SystemScope 값과 동일 (하위 호환) */
export type ScopeGroup = 'primary' | 'secondary' | 'bop';

export const SCOPE_GROUPS: readonly ScopeGroup[] = ['primary', 'secondary', 'bop'] as const;

/**
 * 계통 id: 그룹 자체(그룹 전체) 또는 카탈로그의 `그룹/계통` id.
 * 카탈로그(`as const`)에서 유도하므로 코드에 적은 오타 id 는 컴파일 오류다.
 * (저장 데이터에서 온 미지 문자열은 런타임에 isKnownScope 로 거른다.)
 */
export type ScopeId = ScopeGroup | KnownSystemId;

/** 참가자 역할 — 없으면 `system`(계통 모델) */
export type ModelRole = 'system' | 'control' | 'scenario';

export const MODEL_ROLES: readonly ModelRole[] = ['system', 'control', 'scenario'] as const;

export const ROLE_LABELS: Record<ModelRole, string> = {
  system: '계통 모델',
  control: '제어기',
  scenario: '시나리오',
};

export const ROLE_DESCRIPTIONS: Record<ModelRole, string> = {
  system: '물리 계통을 모델링 — scope는 담당(cover) 계통',
  control: '제어기·운전원 모델 — scope는 작용하는 계통(선택), 커버리지에서 제외',
  scenario: '고장·교란·열원 주입 — scope는 작용하는 계통(선택), 커버리지에서 제외',
};

export interface ScopeSystemDef {
  /** `그룹/계통` */
  id: `${ScopeGroup}/${string}`;
  /** 화면 라벨 (한글 · 영문 약어) */
  label: string;
  /** 짧은 라벨 (칩·그림 캡션) */
  short: string;
}

export interface ScopeGroupDef {
  id: ScopeGroup;
  label: string;
  short: string;
  systems: readonly ScopeSystemDef[];
}

/**
 * 계통 목록은 **KAERI 공식 계통도**(`docs/SMR_도식.pptx`, 2026-10-01 수령)의 그룹별 목록 그대로다 —
 * 그전 목록은 우리가 임의로 적은 것이었다(사용자 결정 2026-10-01: 공식 목록을 그대로 채택). 라벨·약어는
 * 공식 표기이고, 뜻이 같은 계통은 id 를 그대로 둬 저장 데이터가 바뀌지 않는다. 바뀐 계통의 옛 id 는
 * `RETIRED_SCOPE_IDS` 가 후계로 잇는다.
 */
export const SCOPE_CATALOG = [
  {
    id: 'primary',
    label: 'Primary (원자로계통)',
    short: 'Primary',
    systems: [
      { id: 'primary/core', label: '노심 (Reactor Core)', short: 'Core' },
      { id: 'primary/vessel', label: '원자로압력용기 (RPV)', short: 'RPV' },
      { id: 'primary/rcs', label: '원자로냉각재계통 (RCS)', short: 'RCS' },
      { id: 'primary/pressurizer', label: '가압기 (Pressurizer)', short: 'PZR' },
      { id: 'primary/sg', label: '증기발생기 (Steam Generator)', short: 'SG' },
      { id: 'primary/containment', label: '격납용기 (Containment)', short: 'Containment' },
      { id: 'primary/pdhr', label: '피동잔열제거계통 (PDHR)', short: 'PDHR' },
      { id: 'primary/cvcs', label: '화학 및 체적제어계통 (CVCS)', short: 'CVCS' },
      { id: 'primary/scs', label: '정지냉각계통 (SCS)', short: 'SCS' },
      { id: 'primary/gis', label: '중력주입계통 (GIS)', short: 'GIS' },
      { id: 'primary/pis', label: '압력주입계통 (PIS)', short: 'PIS' },
      { id: 'primary/ads', label: '자동감압계통 (ADS)', short: 'ADS' },
      { id: 'primary/ccs', label: '격납냉각계통 (CCS)', short: 'CCS' },
    ],
  },
  {
    id: 'secondary',
    label: 'Secondary (2차 계통)',
    short: 'Secondary',
    systems: [
      { id: 'secondary/mss', label: '주증기계통 (MSS)', short: 'MSS' },
      { id: 'secondary/turbine', label: '터빈 (Turbine)', short: 'TUR' },
      { id: 'secondary/condenser', label: '복수기 (Condenser)', short: 'CNR' },
      { id: 'secondary/feedwater', label: '급수계통 (FWS)', short: 'FWS' },
      { id: 'secondary/afw', label: '보조급수계통 (AFW)', short: 'AFW' },
    ],
  },
  {
    id: 'bop',
    label: 'BOP (보조·전력)',
    short: 'BOP',
    systems: [
      { id: 'bop/generator', label: '발전기 (Generator)', short: 'GEN' },
      { id: 'bop/cw', label: '순환수계통 (CWS)', short: 'CWS' },
      { id: 'bop/els', label: '전력배전·소내부하 (ELS)', short: 'ELS' },
      { id: 'bop/ccw', label: '기기냉각수계통 (CCWS)', short: 'CCWS' },
    ],
  },
] as const satisfies readonly ScopeGroupDef[];

/**
 * 은퇴한 계통 id → 후계 계통. 공식 목록 채택(2026-10-01) 전에 저장된 프로젝트·모델의 scope 가 여기
 * 적힌 id 를 들고 있다. 열 때 후계로 바꾸고(`migrateScopeIds`), 후계가 없는 것(공식에 없는 계통)은
 * 지운다 — 제어기·시나리오의 작용 계통은 선택 항목이라 비어도 되고, 계통 모델은 남는 담당으로 판정한다.
 * - 안전계통 묶음(SIS/PSIS·PRHRS·ADS)은 그 묶음이 실제로 뜻하던 계통으로만 펼친다: SIS/PSIS → GIS·PIS,
 *   PRHRS → PDHR, ADS → ADS. 격납냉각계통(CCS)은 공식에서 새로 생긴 계통이라 옛 선택에 끼워 넣지 않는다
 *   (끼우면 옛 프로젝트가 담당하지 않던 계통을 담당하는 것으로 둔갑한다 — Copilot 지적).
 */
export const RETIRED_SCOPE_IDS: Readonly<Record<string, readonly KnownSystemId[]>> = {
  'primary/rhr': ['primary/scs'],
  'primary/safety': ['primary/pdhr', 'primary/gis', 'primary/pis', 'primary/ads'],
  'primary/rps': [],
  'bop/switchyard': ['bop/els'],
  'bop/ic': [],
};

/** 은퇴한 id 인가 */
export function isRetiredScope(value: string): boolean {
  return Object.prototype.hasOwnProperty.call(RETIRED_SCOPE_IDS, value);
}

/**
 * 저장된 scope 목록의 은퇴 id 를 후계로 바꾼다. 미지 id 는 그대로 둔다(검사기가 오류로 보고한다).
 * 결과는 정규화된 순서·중복 없음.
 *
 * 이행은 **뜻을 넓히지 않는다**: 후계 없는 은퇴 id(RPS·I&C)만으로 된 목록을 비우면 «제한 없음»
 * (`isScopeRestriction([]) === false`)이 되어 제한이 걸려 있던 프로젝트가 조용히 전체로 열린다(게이트 리뷰).
 * 그런 목록은 은퇴 id 를 그대로 둔다 — 카탈로그 밖 id 로 남아 검사가 보고하고 사용자가 다시 고른다.
 */
export function migrateScopeIds(ids: readonly string[]): string[] {
  const out: string[] = [];
  for (const id of ids) {
    if (isRetiredScope(id)) out.push(...RETIRED_SCOPE_IDS[id]);
    else out.push(id);
  }
  if (ids.length > 0 && out.length === 0) return [...new Set(ids)];
  const known = normalizeScopes(out);
  const unknown = out.filter((id) => !isKnownScope(id));
  return [...known, ...new Set(unknown)];
}

/** 카탈로그에 실제로 있는 계통 id 유니온 (컴파일 타임 검사용) */
export type KnownSystemId = (typeof SCOPE_CATALOG)[number]['systems'][number]['id'];

/**
 * 시스템 개요 SVG(ReactorSystemSVG)의 요소 id = 계통 id — 그림과 선택기는 **일대일**이다
 * (2026-08-19 사용자 지적: 그림 6요소 vs 계통 20 불일치 → 요소당 계통 하나로 그림을 재작성).
 */
export type ReactorComponentId = KnownSystemId;

const GROUP_BY_ID: Record<ScopeGroup, ScopeGroupDef> = Object.fromEntries(
  (SCOPE_CATALOG as readonly ScopeGroupDef[]).map((g) => [g.id, g]),
) as Record<ScopeGroup, ScopeGroupDef>;

const SYSTEM_BY_ID: ReadonlyMap<string, ScopeSystemDef> = new Map(
  SCOPE_CATALOG.flatMap((g) => g.systems.map((s) => [s.id, s] as const)),
);

/** 카탈로그 순서 (정규화 정렬용) — 그룹 다음에 그 그룹의 계통 */
const SCOPE_ORDER: ReadonlyMap<string, number> = new Map(
  SCOPE_CATALOG.flatMap((g) => [g.id, ...g.systems.map((s) => s.id)]).map((id, i) => [id, i] as const),
);

/** 시스템 개요 SVG의 요소 전부 = 카탈로그 계통 전부 (dim 계산용) */
export const ALL_SVG_COMPONENTS: readonly ReactorComponentId[] = SCOPE_CATALOG.flatMap((g) =>
  g.systems.map((s) => s.id),
);

/** SVG 요소(=계통) → 그룹 (색상용) */
export function svgComponentGroup(component: ReactorComponentId): ScopeGroup {
  const group = scopeGroupOf(component);
  if (!group) throw new Error(`SVG component not in catalog: ${component}`);
  return group;
}

/** 그룹 id인가 */
export function isScopeGroup(value: string): value is ScopeGroup {
  return (SCOPE_GROUPS as readonly string[]).includes(value);
}

/** 카탈로그에 있는 id인가 (그룹 또는 계통) */
export function isKnownScope(value: string): value is ScopeId {
  return isScopeGroup(value) || SYSTEM_BY_ID.has(value);
}

/** id의 그룹 (`primary/sg` → `primary`, `primary` → `primary`). 미지 값이면 null */
export function scopeGroupOf(id: string): ScopeGroup | null {
  const head = id.split('/')[0];
  return isScopeGroup(head) ? head : null;
}

export function scopeGroupDef(group: ScopeGroup): ScopeGroupDef {
  return GROUP_BY_ID[group];
}

/** 화면 라벨. 그룹 → 그룹 라벨, 계통 → 계통 라벨, 미지 → id 그대로 */
export function scopeLabel(id: string): string {
  if (isScopeGroup(id)) return GROUP_BY_ID[id].label;
  return SYSTEM_BY_ID.get(id)?.label ?? id;
}

/** 칩용 짧은 라벨: 그룹 → `Primary`, 계통 → `Primary › SG` */
export function scopeShortLabel(id: string): string {
  if (isScopeGroup(id)) return GROUP_BY_ID[id].short;
  const sys = SYSTEM_BY_ID.get(id);
  const group = scopeGroupOf(id);
  if (sys && group) return `${GROUP_BY_ID[group].short} › ${sys.short}`;
  return id;
}

/** 담당 목록을 구체 계통 집합으로 전개 — 그룹은 그 그룹의 모든 계통으로 */
export function expandScopes(ids: readonly string[]): Set<string> {
  const out = new Set<string>();
  for (const id of ids) {
    if (isScopeGroup(id)) {
      for (const s of GROUP_BY_ID[id].systems) out.add(s.id);
    } else {
      out.add(id);
    }
  }
  return out;
}

/**
 * `covering`(모델들의 담당 목록 union)이 `target`(프로젝트 scope 항목)을 덮는가.
 * - target 이 계통: 그 계통 자체 또는 그 그룹 전체를 담당하면 cover.
 * - target 이 그룹: 그룹 자체를 담당하거나, 그룹의 모든 계통이 각각 담당되면 cover.
 */
export function isScopeCovered(target: string, covering: readonly string[]): boolean {
  const set = new Set(covering);
  if (set.has(target)) return true;
  if (isScopeGroup(target)) {
    const expanded = expandScopes(covering);
    return GROUP_BY_ID[target].systems.every((s) => expanded.has(s.id));
  }
  const group = scopeGroupOf(target);
  return group !== null && set.has(group);
}

/** 담당 목록이 하이라이트할 SVG 요소 = 그 계통들(그룹이면 그룹의 계통 전부). 미지 id 는 제외 */
export function svgComponentsForScopes(ids: readonly string[]): ReactorComponentId[] {
  return [...expandScopes(ids)].filter((id): id is ReactorComponentId => SYSTEM_BY_ID.has(id));
}

/** 그룹별로 묶어 정렬 (표시용): [{group, ids}] — 그룹 자체가 들어 있으면 그 그룹은 [group] 하나로 */
export function groupScopes(ids: readonly string[]): { group: ScopeGroup; ids: string[] }[] {
  const byGroup = new Map<ScopeGroup, string[]>();
  for (const id of ids) {
    const g = scopeGroupOf(id);
    if (!g) continue;
    const list = byGroup.get(g) ?? [];
    list.push(id);
    byGroup.set(g, list);
  }
  return SCOPE_GROUPS.filter((g) => byGroup.has(g)).map((g) => {
    const list = byGroup.get(g)!;
    return { group: g, ids: list.includes(g) ? [g] : list };
  });
}

/** 역할이 커버리지에 참여하는가 (계통 모델만) */
export function roleCountsForCoverage(role: ModelRole | undefined): boolean {
  return (role ?? 'system') === 'system';
}

// ------------------------------------------------------------------ 선택 편집

/** 정규화: 미지 id 제거·중복 제거·그룹이 있으면 그 그룹의 계통 제거·그룹의 계통이 전부 있으면 그룹으로 접기 */
export function normalizeScopes(ids: readonly string[]): ScopeId[] {
  const set = new Set(ids.filter(isKnownScope));
  for (const g of SCOPE_CATALOG) {
    const allSystems = g.systems.every((s) => set.has(s.id));
    if (allSystems || set.has(g.id)) {
      for (const s of g.systems) set.delete(s.id);
      set.add(g.id);
    }
  }
  // 카탈로그 순서로 정렬 (그룹 → 계통)
  return [...set].sort((a, b) => (SCOPE_ORDER.get(a) ?? 0) - (SCOPE_ORDER.get(b) ?? 0)) as ScopeId[];
}

/** 그룹 전체 토글: 켜면 그룹 id 하나(계통 개별 선택 대체), 끄면 그 그룹의 모든 항목 제거 */
export function toggleGroupScope(ids: readonly string[], group: ScopeGroup): ScopeId[] {
  const rest = ids.filter((id) => scopeGroupOf(id) !== group);
  if (isGroupSelected(ids, group)) return normalizeScopes(rest);
  return normalizeScopes([...rest, group]);
}

/** 계통 하나 토글. 그룹 전체가 선택돼 있던 상태에서 하나를 끄면 "그 그룹의 나머지 계통 전부"로 펼쳐진다 */
export function toggleSystemScope(ids: readonly string[], systemId: string): ScopeId[] {
  const group = scopeGroupOf(systemId);
  if (!group || !isKnownScope(systemId) || isScopeGroup(systemId)) return normalizeScopes(ids);
  const set = new Set(ids);
  if (set.has(group)) {
    // 그룹 전체 → 그 계통만 빼고 나머지 계통으로 전개 (비정규화 입력에 계통이 같이 있어도 확실히 끈다)
    set.delete(group);
    set.delete(systemId);
    for (const s of scopeGroupDef(group).systems) if (s.id !== systemId) set.add(s.id);
    return normalizeScopes([...set]);
  }
  if (set.has(systemId)) set.delete(systemId);
  else set.add(systemId);
  return normalizeScopes([...set]);
}

/** 그룹 전체가 선택된 상태인가 (그룹 id 자체 또는 모든 계통) */
export function isGroupSelected(ids: readonly string[], group: ScopeGroup): boolean {
  const set = new Set(ids);
  if (set.has(group)) return true;
  return scopeGroupDef(group).systems.every((s) => set.has(s.id));
}

/** 그룹의 일부(계통 단위)라도 선택돼 있는가 */
export function isGroupTouched(ids: readonly string[], group: ScopeGroup): boolean {
  return ids.some((id) => scopeGroupOf(id) === group);
}

/** 계통이 선택 상태인가 (직접 또는 그룹 전체를 통해) */
export function isSystemSelected(ids: readonly string[], systemId: string): boolean {
  const group = scopeGroupOf(systemId);
  return ids.includes(systemId) || (group !== null && ids.includes(group));
}

/**
 * `available`(예: 프로젝트 scope) 안에서 고를 수 있는 항목.
 * - 그룹 전체가 available 이면 그 그룹의 모든 계통 + 그룹 자체.
 * - 일부 계통만 available 이면 그 계통들만(그룹 자체는 불가).
 * `available` 이 없으면(프로젝트 scope 편집) 전부.
 */
export function selectableSystems(group: ScopeGroup, available?: readonly string[]): ScopeSystemDef[] {
  const def = scopeGroupDef(group);
  if (!available) return [...def.systems];
  if (available.includes(group)) return [...def.systems];
  const set = new Set(available);
  return def.systems.filter((s) => set.has(s.id));
}

export function isGroupWholeSelectable(group: ScopeGroup, available?: readonly string[]): boolean {
  return !available || available.includes(group);
}

/** `available`(프로젝트 scope)이 실제로 제한을 뜻하는가 — 없거나 비어 있으면 "제한 없음"(프로젝트 scope 미정) */
export function isScopeRestriction(available: readonly string[] | undefined): available is readonly string[] {
  return !!available && available.length > 0;
}

/**
 * `ids`(파티션/모델 scope)를 `available`(프로젝트 scope) 안으로 제한. 제한이 없으면(미정) 정규화만.
 * - available 이 덮는 항목은 유지.
 * - ids 의 그룹 전체가 available 엔 그 그룹의 일부 계통만 있으면 → 그 계통들로 축소.
 * - 그 외는 제거.
 */
export function restrictScopes(ids: readonly string[], available: readonly string[] | undefined): ScopeId[] {
  if (!isScopeRestriction(available)) return normalizeScopes(ids);
  const out: string[] = [];
  for (const id of ids) {
    if (isScopeCovered(id, available)) {
      out.push(id);
    } else if (isScopeGroup(id)) {
      out.push(...available.filter((a) => scopeGroupOf(a) === id));
    }
  }
  return normalizeScopes(out);
}

/** 정규형 목록 둘이 같은가(위치 비교 — normalizeScopes 를 거친 값끼리) */
export function sameScopes(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((id, i) => id === b[i]);
}

/** 역할이 받는 제한 — 계통 모델(커버리지 참여)만 프로젝트 scope, 제어기·시나리오는 제한 없음(undefined) */
export function availableForRole<T extends readonly string[]>(
  role: ModelRole | undefined,
  available: T | undefined,
): T | undefined {
  return roleCountsForCoverage(role) ? available : undefined;
}

/** 역할별 제한: 계통 모델만 프로젝트 scope 안으로 자르고, 제어기·시나리오는 정규화만 */
export function restrictScopesForRole(
  ids: readonly string[],
  role: ModelRole | undefined,
  available: readonly string[] | undefined,
): ScopeId[] {
  return restrictScopes(ids, availableForRole(role, available));
}

/**
 * 모델 scope 규칙 — 생성(위저드·새 모델)·편집(모델 홈) 공통 SSOT. 위반이면 메시지, 아니면 null.
 * - 공통: 카탈로그에 없는 id 는 거절(정규화를 거치지 않은 저장/가져오기 값도 여기서 걸린다).
 * - 계통 모델: 담당 계통 1개 이상, (프로젝트 scope 가 정해져 있으면) 전부 그 안.
 * - 제어기·시나리오: 작용 계통은 선택이며 프로젝트 밖도 허용 → 제약 없음.
 */
export function validateModelScope(
  role: ModelRole | undefined,
  systems: readonly string[],
  available: readonly string[] | undefined,
): string | null {
  if (!systems.every(isKnownScope)) return '알 수 없는 계통 id 가 포함돼 있습니다.';
  if (!roleCountsForCoverage(role)) return null;
  if (systems.length === 0) return '계통 모델은 담당 계통을 하나 이상 골라야 합니다.';
  if (isScopeRestriction(available) && !systems.every((id) => isScopeCovered(id, available))) {
    return '프로젝트 scope 밖의 계통이 포함돼 있습니다.';
  }
  return null;
}

/** 프로젝트 scope 중 `covering`(계통 모델들의 담당 union)이 덮지 못한 항목. 미지 id 는 덮일 수 없으니 포함 */
export function uncoveredScopes(projectScope: readonly string[], covering: readonly string[]): string[] {
  return projectScope.filter((id) => !isKnownScope(id) || !isScopeCovered(id, covering));
}

/** 저장 규약: 기본 역할(system)은 저장하지 않는다 — 쓰기 지점 공용 */
export function storedRole(role: ModelRole | undefined): ModelRole | undefined {
  return role && role !== 'system' ? role : undefined;
}

/** 저장 객체에 펼칠 role 필드 — `{ ...roleFields(role) }` (system 이면 필드 없음) */
export function roleFields(role: ModelRole | undefined): { role?: ModelRole } {
  const r = storedRole(role);
  return r ? { role: r } : {};
}

/** 커버리지에 참여하는 참가자(역할 system)들의 담당 scope union.
 *  파티션(`scope: string[]`)과 모델(`scope: { systems }`) 둘 다 받는다. */
export function coveringScopes(
  items: readonly { role?: ModelRole; scope?: readonly string[] | { systems?: readonly string[] } | null }[],
): string[] {
  return items
    .filter((it) => roleCountsForCoverage(it.role))
    .flatMap((it) => {
      const sc = it.scope;
      if (!sc) return [];
      return Array.isArray(sc) ? [...sc] : [...((sc as { systems?: readonly string[] }).systems ?? [])];
    });
}

/** 계통의 짧은 캡션(그림·칩) — 그룹 접두 없이 */
export function scopeSystemShort(id: string): string {
  return SYSTEM_BY_ID.get(id)?.short ?? id;
}

/** 여러 계통을 한꺼번에 on/off (그룹 전체가 선택 상태면 먼저 계통으로 전개) */
export function setSystemsSelected(ids: readonly string[], systemIds: readonly string[], on: boolean): ScopeId[] {
  const expanded = new Set(expandScopes(ids.filter(isKnownScope)));
  for (const id of systemIds) {
    if (on) expanded.add(id);
    else expanded.delete(id);
  }
  return normalizeScopes([...expanded]);
}
